import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stIntelCategoriesConfig,
  stUpdateIntelCategories,
  StIntelCategory,
} from '../../../services/socialTradingService';

const CODE_RE = /^[a-z][a-z0-9_]{1,31}$/;

export default function SocialTradingIntelCategoriesList() {
  const t = useTranslate();
  const notify = useNotify();
  const [rows, setRows] = useState<StIntelCategory[]>([]);

  const load = useCallback(async () => {
    try {
      const data = await stIntelCategoriesConfig();
      setRows([...(data?.categories || [])].sort((a, b) => a.sort - b.sort || a.code.localeCompare(b.code)));
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (idx: number, patch: Partial<StIntelCategory>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    try {
      if (!rows.length) {
        notify(t('module.socialTrading.intelCategoriesEmpty'), { type: 'warning' });
        return;
      }
      // reduce rather than for…of: `no-restricted-syntax` bans the loop. The `found` guard keeps
      // the original behaviour of reporting the earliest offending row.
      const seen = new Set<string>();
      const error = rows.reduce<string | null>((found, r) => {
        if (found) return found;
        const code = r.code.trim();
        const name = r.name.trim();
        if (!CODE_RE.test(code)) return 'module.socialTrading.intelCategoriesCodeInvalid';
        if (!name || name.length > 64) return 'module.socialTrading.intelCategoriesNameInvalid';
        if (!Number.isInteger(r.sort) || r.sort < 1) {
          return 'module.socialTrading.intelCategoriesSortInvalid';
        }
        if (seen.has(code)) return 'module.socialTrading.intelCategoriesDuplicate';
        seen.add(code);
        return null;
      }, null);

      if (error) {
        notify(t(error), { type: 'warning' });
        return;
      }
      const next = await stUpdateIntelCategories({
        categories: rows.map((r) => ({
          code: r.code.trim(),
          name: r.name.trim(),
          sort: r.sort,
        })),
      });
      setRows([...(next?.categories || [])]);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  return (
    <PermissionGuard resource="st-intel-categories" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.intelCategories')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.groupIntel') },
            { name: t('module.socialTrading.intelCategories') },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={load}>
                {t('module.socialTrading.refresh')}
              </Button>
              <PermissionButton resource="st-intel-categories" action="update">
                <Button size="small" variant="contained" onClick={save}>
                  {t('module.socialTrading.save')}
                </Button>
              </PermissionButton>
            </Stack>
          }
        />
        <Stack spacing={2} sx={{ mt: 2, maxWidth: 900 }}>
          <Typography variant="body2" color="text.secondary">
            {t('module.socialTrading.intelCategoriesHint')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={200}>{t('module.socialTrading.colType')}</TableCell>
                <TableCell>{t('module.socialTrading.intelCategoriesName')}</TableCell>
                <TableCell width={100}>{t('module.socialTrading.intelCategoriesSort')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={row.code || idx}>
                  <TableCell>
                    <TextField size="small" fullWidth value={row.code} disabled />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.name}
                      onChange={(e) => updateRow(idx, { name: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={row.sort}
                      onChange={(e) => updateRow(idx, { sort: Number(e.target.value) })}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
}
